import {
    Container,
    ContainerProps,
    Divider,
    IconButton,
    LinearProgress,
    ListItemAvatar,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    MenuItem,
    MenuList,
    Skeleton,
} from '@mui/material';
import { useEffect } from 'react';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import { useSession } from '@/providers/session-provider';
import { useQuery } from '@apollo/client';
import { WorkspacesResumeDocument } from '@/graphql/generated/workspaces-resumes.generated';
import { useSwitchWorkspace } from '@/hooks/use-switch-workspace';
import { styled } from '@mui/material/styles';
import WorkspaceAvatar from './workspace/WorkspaceAvatar';
import { useTranslation } from 'react-i18next';

const StyledContainer = styled(Container)<ContainerProps>(() => ({
    '&.MuiContainer-root': {
        padding: 0,
    },
}));

const LoadWorkspacesSkeleton = () => {
    return (
        <MenuItem dense sx={{ padding: '0.375rem' }}>
            <ListItemIcon>
                <IconButton>
                    <DragIndicatorRoundedIcon />
                </IconButton>
            </ListItemIcon>
            <ListItemAvatar>
                <Skeleton variant="rounded" sx={{ width: '2rem', height: '2rem' }} animation="wave" />
            </ListItemAvatar>
            <ListItemText
                primary={<Skeleton variant="text" animation="wave" width={'50%'} />}
                secondary={<Skeleton variant="text" animation="wave" width={'50%'} />}
                secondaryTypographyProps={{ variant: 'caption' }}
            />
        </MenuItem>
    );
};

export default function WorkspaceSwitcher({
    popOverLoadingComplete,
    onSwitch,
}: {
    popOverLoadingComplete?: () => void;
    onSwitch?: () => void;
}) {
    const { t } = useTranslation();
    const {
        me,
        workspace: currentWorkspace,
        logout: { logout, loading: loadingLogout },
    } = useSession();
    const { switchWorkspace, switching } = useSwitchWorkspace();

    const {
        data,
        loading: loadingWorkspaces,
        error,
        fetchMore,
    } = useQuery(WorkspacesResumeDocument, {
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        if (popOverLoadingComplete) {
            popOverLoadingComplete();
        }
    }, [loadingWorkspaces, popOverLoadingComplete, data]);

    const loadMore = () => {
        fetchMore({ variables: { skip: data?.workspaces.items?.length } });
    };

    const handleSwitch = (workspace: { id: string }) => {
        if (workspace.id === currentWorkspace.id) {
            // Already here — nothing to switch to, just dismiss.
            onSwitch?.();
            return;
        }
        // The popover is deliberately left open. This component owns the mutation, so
        // closing it would unmount the hook that holds `switching` and the concurrency
        // guard — and the disabled row is the only feedback there is until the page
        // reloads over the whole thing a moment later.
        switchWorkspace({ id: workspace.id });
    };

    return (
        <MenuList sx={{ width: '100%' }}>
            <StyledContainer sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                <MenuItem dense data-id="menu-account-your-account">
                    <ListItemText>{me.email}</ListItemText>
                    {/*<ListItemIcon>
                        <MoreButton />
                    </ListItemIcon>*/}
                </MenuItem>
                {data?.workspaces?.items?.map((workspace) => (
                    <MenuItem
                        key={workspace.id}
                        dense
                        disabled={switching}
                        sx={{ padding: '0.375rem' }}
                        onClick={() => handleSwitch(workspace)}
                        data-id={`menu-account-workspace-${workspace.id}`}
                    >
                        <ListItemIcon>
                            <IconButton>
                                <DragIndicatorRoundedIcon />
                            </IconButton>
                        </ListItemIcon>
                        <ListItemAvatar>
                            <WorkspaceAvatar
                                {...workspace}
                                sx={(theme) => ({
                                    width: theme.spacing(4),
                                    height: theme.spacing(4),
                                    fontSize: theme.spacing(1.5),
                                })}
                            />
                        </ListItemAvatar>
                        <ListItemText
                            primary={workspace.name}
                            secondary={`${workspace.plan?.name} • ${workspace.totalMembers} members`}
                            secondaryTypographyProps={{ variant: 'caption' }}
                        />

                        <ListItemSecondaryAction sx={{ right: '0.5rem' }}>
                            {workspace.id === currentWorkspace.id && <DoneRoundedIcon color="primary" />}
                            {/*<RelativeBadge badgeContent="100" color="error" />*/}
                        </ListItemSecondaryAction>
                    </MenuItem>
                ))}

                {loadingWorkspaces && <LoadWorkspacesSkeleton />}

                {error && !loadingWorkspaces && (
                    <MenuItem dense disabled>
                        <ListItemText>{t('error.something-wrong')}</ListItemText>
                    </MenuItem>
                )}

                {data?.workspaces.hasMore && !loadingWorkspaces && (
                    <MenuItem dense onClick={loadMore}>
                        <ListItemText>{t('str.load-more')}</ListItemText>
                    </MenuItem>
                )}
                <Divider />
            </StyledContainer>
            {/*<MenuItem dense>
                <ListItemText>Add another account</ListItemText>
            </MenuItem>*/}
            <MenuItem dense onClick={() => logout()} data-id="menu-account-logout">
                <ListItemText>{loadingLogout ? <LinearProgress /> : t('str.action.logout')}</ListItemText>
            </MenuItem>
        </MenuList>
    );
}
